import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const ShopContext = createContext();

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within ShopProvider');
  }
  return context;
};

const EAST_AFRICA_CODES = new Set(['KE', 'TZ', 'UG', 'RW', 'ET', 'BI', 'SS', 'SO', 'DJ', 'ER']);
const REGION_CACHE_KEY = 'rastyle_user_region_v2';
const STORE_CACHE_KEY = 'rastyle_current_store_v2';

export const ShopProvider = ({ children }) => {
  const [currentStore, setCurrentStore] = useState(null);
  const [userRegion, setUserRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initializeShop = useCallback(async () => {
    try {
      setLoading(true);

      const cachedRegion = localStorage.getItem(REGION_CACHE_KEY);
      const cachedStore = localStorage.getItem(STORE_CACHE_KEY);
      const cacheTime = localStorage.getItem('rastyle_cache_time');

      if (cachedRegion && cachedStore && cacheTime) {
        const cacheAge = Date.now() - parseInt(cacheTime);
        if (cacheAge < 3600000) {
          setUserRegion(JSON.parse(cachedRegion));
          setCurrentStore(JSON.parse(cachedStore));
          setLoading(false);
          return;
        }
      }

      const regionResponse = await axios.get(`${API}/api/geo/detect`);
      const region = regionResponse.data;
      setUserRegion(region);

      const countryCode = region.country_code || 'US';
      const storeResponse = await axios.get(`${API}/api/shop/store-for-region?country_code=${countryCode}`);
      const store = storeResponse.data.store;
      setCurrentStore(store);

      localStorage.setItem(REGION_CACHE_KEY, JSON.stringify(region));
      localStorage.setItem(STORE_CACHE_KEY, JSON.stringify(store));
      localStorage.setItem('rastyle_cache_time', Date.now().toString());
    } catch (err) {
      console.error('Error initializing shop:', err);
      setError(err.message);
      try {
        const fallbackResponse = await axios.get(`${API}/api/shop/store-for-region?country_code=US`);
        setCurrentStore(fallbackResponse.data.store);
      } catch (fallbackErr) {
        console.error('Fallback store fetch failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeShop();
  }, [initializeShop]);

  const switchStore = useCallback(async (countryCode) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/shop/store-for-region?country_code=${countryCode}`);
      const store = response.data.store;
      setCurrentStore(store);

      localStorage.setItem(STORE_CACHE_KEY, JSON.stringify(store));
      localStorage.setItem('rastyle_cache_time', Date.now().toString());
      return store;
    } catch (err) {
      console.error('Error switching store:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getShopUrl = useCallback(() => {
    if (!currentStore) return 'https://rastyle.shop';
    if (currentStore.store_type === 'afrika_shop') return '/afrika-shop';
    return `https://${currentStore.domain}`;
  }, [currentStore]);

  const getCheckoutUrl = useCallback((variantId, quantity = 1) => {
    if (!currentStore) return null;
    if (currentStore.store_type === 'afrika_shop') return null;
    return `https://${currentStore.domain}/cart/${variantId}:${quantity}`;
  }, [currentStore]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(REGION_CACHE_KEY);
    localStorage.removeItem(STORE_CACHE_KEY);
    localStorage.removeItem('rastyle_cache_time');
    initializeShop();
  }, [initializeShop]);

  const isEastAfrica = EAST_AFRICA_CODES.has(userRegion?.country_code);
  const isAfrikaShop = currentStore?.store_type === 'afrika_shop';

  const value = {
    currentStore,
    userRegion,
    loading,
    error,
    switchStore,
    getShopUrl,
    getCheckoutUrl,
    clearCache,
    isEastAfrica,
    isAfrica: isEastAfrica,
    isAfrikaShop,
    isWooCommerce: false,
    currency: currentStore?.currency || 'USD',
    storeType: currentStore?.store_type || 'shopify',
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContext;
